<template>
  <div>
    <v-overlay :value="isProcessing">
      <v-progress-circular
        indeterminate
        color="primary"
        :size="70"
        :width="7"
      ></v-progress-circular>
    </v-overlay>
    <UniqueEmailDialog
      :showDialog="showNonUniqueEmail"
      :onRecover="onRecoverEmail"
      :email="email"
      :recoverable="recoverableEmail"
      :onCheck="checkEmail"
      :onClose="() => showNonUniqueEmail = false"
    />
    <UniqueUsernameDialog
      :showDialog="showNonUniqueUsername"
      :username="username"
      :onCheck="checkUsername"
      :onClose="() => showNonUniqueUsername = false"
    />
    <v-row justify="center">
      <v-col lg="6" md="5" class="pb-0 hidden-sm-and-down">
        <v-img class="sticky-image" height="100vh" :src="createAccountImage" />
      </v-col>
      <v-col class="register" lg="6" md="7" sm="12">
        <v-alert
          width="100%"
          v-model="isError"
          dismissible
          type="error"
        >{{ errorMessage }}</v-alert>
        <h1 class="register-title">Create your ASCA web account.</h1>
        <p>
          Already a member? You'll be asked to link your account in the next step.
        </p>
        <v-form v-model="isValid" class="register-form">
          <v-text-field
            outlined
            required
            label="First Name"
            type="text"
            v-model="firstName"
            :rules="[(v) => !!v || 'First name is required']"
          ></v-text-field>
          <v-text-field
            outlined
            required
            label="Last Name"
            type="text"
            v-model="lastName"
            :rules="[(v) => !!v || 'Last name is required']"
          ></v-text-field>
          <v-text-field
            outlined
            required
            label="Create Username"
            type="text"
            v-model="username"
            :rules="[(v) => !!v || 'Username is required']"
          ></v-text-field>
          <v-text-field
            outlined
            required
            label="Create Password"
            type="password"
            v-model="password1"
            :rules="passwordRules"
          ></v-text-field>
          <PasswordRules :password="password1" />
          <v-text-field
            outlined
            required
            label="Confirm Password"
            type="password"
            v-model="password2"
            :rules="[
              (v) => !!v || 'Confirm password is required',
              (v) => v === password1 || 'Passwords do not match'
            ]"
          ></v-text-field>
          <v-text-field
            outlined
            required
            label="Email"
            type="email"
            v-model="email"
            :error-messages="validEmail == null || validEmail ? null : ['Email must be valid']"
            :rules="[(v) => !!v || 'Email is required']"
          ></v-text-field>
          <InternationalPhoneNumberInput
            required
            @changeData="(v) => phone = v.number.e164"
          />
          <div>
            <v-checkbox
              outlined
              required
              class="signup-under-18-checkbox"
              label="This is a Junior Account (under 18 years old)"
              type="check"
              v-model="isJunior"
            ></v-checkbox>
          </div>
          <DatepickerFormatted
            v-if="isJunior"
            :date="birthDate"
            label="Birth Date"
            v-on:updateDates="updateDates($event, 'birthDate')"
            :rules="[validateIsJunior]"
          />
        </v-form>
        <v-btn
          :dark="canSubmit()"
          large
          color="primary"
          v-on:click="submit()"
          :disabled="!canSubmit()"
        >Submit</v-btn>
        <p class="pt-3" >Already enrolled? <a href="/login">Login</a></p>

        <div class="footer-container">
          <v-img height="2rem" contain src="../../assets/logo.svg" />
          <footer-text />
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import decodeToken from "@/helpers/decode-token";
import { debounce } from "throttle-debounce";

import DatepickerFormatted from '@/components/common/inputs/DatepickerFormatted.vue';
import UniqueEmailDialog from '@/components/common/UniqueEmailDialog.vue';
import UniqueUsernameDialog from '@/components/common/UniqueUsernameDialog.vue';
import FooterText from '@/components/common/FooterText.vue';
import PasswordRules from '@/components/common/PasswordRules.vue';
import InternationalPhoneNumberInput from '@/components/common/inputs/InternationalPhoneNumberInput.vue';

import validateEmail from '@/helpers/validate-email';
import passwordValidators from '@/helpers/validate-password';
import { isJunior, isUnder16 } from '@/helpers/validate-junior-age';

import authenticationService from "../../services/authenticationService";
import { getErrorMessage } from '@/services/errorHandler';

const createAccountImage = require('@/assets/create-account.png');

export default Vue.extend({
  components: { FooterText, DatepickerFormatted, UniqueEmailDialog, UniqueUsernameDialog, PasswordRules, InternationalPhoneNumberInput },
  name: 'Signup',
  data() {
    return {
      createAccountImage,

      isValid: true,
      isError: false,
      isProcessing: false,
      errorMessage: '',

      recoverableEmail: false,
      showNonUniqueEmail: false,
      showNonUniqueUsername: false,

      showResults: [] as any[],

      dialog: false,

      firstName: '',
      lastName: '',
      username: '',
      password1: '',
      password2: '',
      email: '',
      validEmail: null,
      phone: '',
      isJunior: false,
      birthDate: null as Temporal.PlainDate | null,
      passwordRules: passwordValidators,
    };
  },
  methods: {
    processSignup: async function (res: any) {
      const decoded: any = decodeToken(res.token);
      const event = new CustomEvent("userLoaded", {
          detail: { personId: decoded.nameid, token: res.token },
      });
      localStorage.setItem("userToken", res.token);
      window.dispatchEvent(event);
    },
    checkEmail: async function(newEmail?: string) {
      if (!!newEmail) this.email = newEmail;

      try {
        const response = await authenticationService.uniqueEmail(this.email);

        this.showNonUniqueEmail = !response.unique;
        this.recoverableEmail = !!response.recoverable;

        return response.unique;
      } catch (error) {
        this.isError = true;
        this.errorMessage = getErrorMessage(error);
        return false;
      }
    },
    checkUsername: async function(newUsername?: string) {
      if (!!newUsername) this.username = newUsername;

      try {
        const uniqueUsername = await authenticationService.uniqueUsername(this.username);

        this.showNonUniqueUsername = !uniqueUsername;

        return uniqueUsername;
      } catch (error) {
        this.isError = true;
        this.errorMessage = getErrorMessage(error);
        return false;
      }
    },
    submit: async function() {
      this.isProcessing = true;

      try {
        if (!validateEmail(this.email)) {
          (this.validEmail as any) = false;
          return;
        }

        const uniqueUsername = await this.checkUsername();
        let uniqueEmail = false;

        if (uniqueUsername) {
          uniqueEmail = await this.checkEmail();
        }

        if (uniqueUsername && uniqueEmail) {
          const response = await authenticationService.createAccount({
          firstName: this.firstName,
          lastName: this.lastName,
          username: this.username,
          password1: this.password1,
          password2: this.password2,
          email: this.email,
          phone: this.phone,
          isJunior: this.isJunior,
          birthDate: this.birthDate || undefined,
        });

          if (response.errors != null) {
            this.isError = true;
            this.errorMessage = response.errors ? response.errors.join(',') : 'An Unknown Error Occured';
          } else if (this.isJunior && isUnder16(this.birthDate!)) {

            this.$store.commit('changeParentVerification', { juniorId: response.personId, firstName: this.firstName, lastName: this.lastName });
            this.$router.push('/parent-member');
          } else {
            await this.processSignup(response);

            this.$store.commit('changeAgreementFor', 'NewAccount');
            this.$router.push('/agreements');
          }
        }
      } catch (error) {
        this.isError = true;
        this.errorMessage = getErrorMessage(error);
      } finally {
        this.isProcessing = false;
      }
    },
    validateIsJunior(birthDate: Temporal.PlainDate | null) {
      return (
        (!!birthDate && isJunior(birthDate))
        || 'Must be under 18 years old'
      )
    },
    canSubmit() {
      return this.isValid && this.validEmail && !this.isProcessing && (this.isJunior ? !!this.birthDate : true)
    },
    updateDates: function (val: any, key: string, item: any) {
      this.birthDate = val.date;
      //workaround to force rerendering after setting up formatted date
      this.$set(this, "showResults", this.showResults);
    },
    onRecoverEmail() {
      this.$store.commit('changeRecoverInfo', {
        email: this.email,
        username: this.username,
        password: this.password1,
        confirmPassword: this.password2,
      });
      this.$router.push('/recover-account');
    }
  },
  watch: {
    email: {
      handler: debounce(
        500,
        function (this: any, newVal: string, oldVal: string) {
          if (newVal === oldVal || !newVal) return;

          if (!newVal) this.validEmail = null;
          else this.validEmail = validateEmail(newVal);
        }
      ),
      deep: true,
    },
  }
});
</script>
